import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { create } from "zustand"

// ==================== Billing Alerts ====================

export type AlertType = "THRESHOLD" | "ANOMALY" | "FORECAST" | "PAYMENT_DUE" | "PAYMENT_FAILED"
export type AlertStatus = "ACTIVE" | "TRIGGERED" | "RESOLVED" | "DISABLED"

export interface BillingAlert {
  id: string
  user_email: string
  aws_account_id: string | null
  alert_type: AlertType
  threshold_amount: string | null
  threshold_percentage: string | null
  status: AlertStatus
  is_active: boolean
  email_notification: boolean
  sms_notification: boolean
  webhook_url: string
  last_triggered_at: string | null
  trigger_count: number
  sns_topic_arn: string
  description: string
  created_at: string
  updated_at: string
}

export interface BillingAlertPayload {
  alert_type: AlertType
  threshold_amount?: number | null
  threshold_percentage?: number | null
  is_active?: boolean
  email_notification?: boolean
  sms_notification?: boolean
  webhook_url?: string
  description?: string
}

// ==================== AWS Accounts ====================

export interface AwsAccount {
  id: string
  user_email: string
  aws_account_id: string
  aws_account_name: string
  is_organization_master: boolean
  parent_account: string | null
  is_active: boolean
  last_sync_at: string | null
  sync_status: string
  sync_error_message: string
  aws_region: string
  created_at: string
  updated_at: string
}

export interface AwsAccountCreatePayload {
  aws_account_id: string
  aws_account_name: string
  iam_role_arn: string
  external_id: string
  aws_region?: string
  is_organization_master?: boolean
}

// ==================== Invoices ====================

export type InvoiceStatus = "DRAFT" | "PENDING" | "PROCESSING" | "PAID" | "FAILED" | "CANCELLED" | "REFUNDED"

export interface Invoice {
  id: string
  invoice_number: string
  billing_period_start: string
  billing_period_end: string
  total_amount: string
  currency: string
  status: InvoiceStatus
  due_date: string
  is_overdue: boolean
  issued_at: string
}

export interface InitiatePaymentResponse {
  payment_id: string
  payment_intent_id: string
  client_secret: string
  status: string
  amount: string
  currency: string
}

// ==================== Payments ====================

export type PaymentStatus = "PENDING" | "PROCESSING" | "SUCCEEDED" | "FAILED" | "REFUNDED" | "PARTIALLY_REFUNDED" | "CANCELLED"
export type PaymentMethod = "CARD" | "ACH" | "WIRE" | "OTHER"

export interface Payment {
  id: string
  user_email: string
  invoice_number: string
  amount: string
  currency: string
  payment_method: PaymentMethod
  status: PaymentStatus
  stripe_payment_intent_id_masked: string | null
  aws_transfer_amount: string
  cloudspry_amount: string
  attempted_at: string
  completed_at: string | null
  failure_code: string
  failure_message: string
  refunded_amount: string
  refunded_at: string | null
  created_at: string
  updated_at: string
}

// ==================== Billing Snapshots ====================

export interface ServiceCost {
  id: string
  service_name: string
  service_code: string
  cost: string
  usage_quantity: string | null
  usage_type: string
  region: string
}

export interface BillingSnapshotListItem {
  id: string
  aws_account_id: string
  period_start: string
  period_end: string
  granularity: "DAILY" | "MONTHLY" | "HOURLY"
  total_cost: string
  currency: string
  is_estimated: boolean
  data_as_of: string
}

export interface BillingSnapshotDetail extends BillingSnapshotListItem {
  aws_account_name: string
  unblended_cost: string
  blended_cost: string
  service_breakdown: ServiceCost[]
  usage_unit: string
  created_at: string
  updated_at: string
}

export interface SnapshotTrendPoint {
  month: string
  total_cost: number
  aws_account_id: string
  currency: string
}

// ==================== Store ====================

interface BillingStore {
  // Alerts
  alerts: BillingAlert[]
  alertsLoading: boolean
  alertsError: string | null
  fetchAlerts: () => Promise<void>
  createAlert: (payload: BillingAlertPayload) => Promise<void>
  updateAlert: (id: string, payload: Partial<BillingAlertPayload>) => Promise<void>
  deleteAlert: (id: string) => Promise<void>
  testAlert: (id: string) => Promise<void>

  // AWS Accounts
  awsAccounts: AwsAccount[]
  awsAccountsLoading: boolean
  awsAccountsError: string | null
  fetchAwsAccounts: () => Promise<void>
  createAwsAccount: (payload: AwsAccountCreatePayload) => Promise<void>
  deleteAwsAccount: (id: string) => Promise<void>
  syncAwsAccount: (id: string) => Promise<void>
  validateAwsAccount: (id: string) => Promise<void>

  // Invoices
  invoices: Invoice[]
  invoicesLoading: boolean
  invoicesError: string | null
  fetchInvoices: () => Promise<void>
  initiatePayment: (invoiceId: string) => Promise<InitiatePaymentResponse | null>

  // Payments
  payments: Payment[]
  paymentsLoading: boolean
  paymentsError: string | null
  fetchPayments: () => Promise<void>

  // Snapshots
  snapshots: BillingSnapshotListItem[]
  snapshotsLoading: boolean
  snapshotsError: string | null
  fetchSnapshots: () => Promise<void>
  selectedSnapshot: BillingSnapshotDetail | null
  selectedSnapshotLoading: boolean
  fetchSnapshotDetail: (id: string) => Promise<void>
  trend: SnapshotTrendPoint[]
  trendLoading: boolean
  fetchTrend: (months?: number) => Promise<void>
}

export const useBillingStore = create<BillingStore>((set) => ({
  // ==================== Alerts ====================
  alerts: [],
  alertsLoading: false,
  alertsError: null,

  fetchAlerts: async () => {
    set({ alertsLoading: true, alertsError: null })
    try {
      const data = await httpClientInstance.get<BillingAlert[]>(API_ENDPOINTS.billing.alerts.list)
      set({ alerts: data, alertsLoading: false })
    } catch (error) {
      set({
        alertsError: error instanceof Error ? error.message : "Failed to fetch billing alerts",
        alertsLoading: false,
      })
    }
  },

  createAlert: async (payload) => {
    set({ alertsLoading: true, alertsError: null })
    try {
      const created = await httpClientInstance.post<BillingAlert>(API_ENDPOINTS.billing.alerts.create, payload)
      set((state) => ({ alerts: [...state.alerts, created], alertsLoading: false }))
    } catch (error) {
      set({
        alertsError: error instanceof Error ? error.message : "Failed to create billing alert",
        alertsLoading: false,
      })
      throw error
    }
  },

  updateAlert: async (id, payload) => {
    set({ alertsLoading: true, alertsError: null })
    try {
      const updated = await httpClientInstance.put<BillingAlert>(API_ENDPOINTS.billing.alerts.update(id), payload)
      set((state) => ({
        alerts: state.alerts.map((a) => (a.id === id ? updated : a)),
        alertsLoading: false,
      }))
    } catch (error) {
      set({
        alertsError: error instanceof Error ? error.message : "Failed to update billing alert",
        alertsLoading: false,
      })
      throw error
    }
  },

  deleteAlert: async (id) => {
    set({ alertsLoading: true, alertsError: null })
    try {
      await httpClientInstance.delete(API_ENDPOINTS.billing.alerts.delete(id))
      set((state) => ({
        alerts: state.alerts.filter((a) => a.id !== id),
        alertsLoading: false,
      }))
    } catch (error) {
      set({
        alertsError: error instanceof Error ? error.message : "Failed to delete billing alert",
        alertsLoading: false,
      })
      throw error
    }
  },

  testAlert: async (id) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.billing.alerts.test(id), {})
    } catch (error) {
      throw error instanceof Error ? error : new Error("Failed to send test alert notification")
    }
  },

  // ==================== AWS Accounts ====================
  awsAccounts: [],
  awsAccountsLoading: false,
  awsAccountsError: null,

  fetchAwsAccounts: async () => {
    set({ awsAccountsLoading: true, awsAccountsError: null })
    try {
      const data = await httpClientInstance.get<AwsAccount[]>(API_ENDPOINTS.billing.awsAccounts.list)
      set({ awsAccounts: data, awsAccountsLoading: false })
    } catch (error) {
      set({
        awsAccountsError: error instanceof Error ? error.message : "Failed to fetch AWS accounts",
        awsAccountsLoading: false,
      })
    }
  },

  createAwsAccount: async (payload) => {
    set({ awsAccountsLoading: true, awsAccountsError: null })
    try {
      const created = await httpClientInstance.post<AwsAccount>(API_ENDPOINTS.billing.awsAccounts.create, payload)
      set((state) => ({ awsAccounts: [...state.awsAccounts, created], awsAccountsLoading: false }))
    } catch (error) {
      set({
        awsAccountsError: error instanceof Error ? error.message : "Failed to connect AWS account",
        awsAccountsLoading: false,
      })
      throw error
    }
  },

  deleteAwsAccount: async (id) => {
    set({ awsAccountsLoading: true, awsAccountsError: null })
    try {
      await httpClientInstance.delete(API_ENDPOINTS.billing.awsAccounts.delete(id))
      set((state) => ({
        awsAccounts: state.awsAccounts.filter((a) => a.id !== id),
        awsAccountsLoading: false,
      }))
    } catch (error) {
      set({
        awsAccountsError: error instanceof Error ? error.message : "Failed to disconnect AWS account",
        awsAccountsLoading: false,
      })
      throw error
    }
  },

  syncAwsAccount: async (id) => {
    set({ awsAccountsLoading: true, awsAccountsError: null })
    try {
      await httpClientInstance.post(API_ENDPOINTS.billing.awsAccounts.sync(id), {})
      const updated = await httpClientInstance.get<AwsAccount[]>(API_ENDPOINTS.billing.awsAccounts.list)
      set({ awsAccounts: updated, awsAccountsLoading: false })
    } catch (error) {
      set({
        awsAccountsError: error instanceof Error ? error.message : "Failed to sync AWS account",
        awsAccountsLoading: false,
      })
      throw error
    }
  },

  validateAwsAccount: async (id) => {
    try {
      await httpClientInstance.post(API_ENDPOINTS.billing.awsAccounts.validate(id), {})
    } catch (error) {
      throw error instanceof Error ? error : new Error("AWS credentials validation failed")
    }
  },

  // ==================== Invoices ====================
  invoices: [],
  invoicesLoading: false,
  invoicesError: null,

  fetchInvoices: async () => {
    set({ invoicesLoading: true, invoicesError: null })
    try {
      const data = await httpClientInstance.get<Invoice[]>(API_ENDPOINTS.billing.invoices.list)
      set({ invoices: data, invoicesLoading: false })
    } catch (error) {
      set({
        invoicesError: error instanceof Error ? error.message : "Failed to fetch invoices",
        invoicesLoading: false,
      })
    }
  },

  initiatePayment: async (invoiceId) => {
    set({ invoicesLoading: true, invoicesError: null })
    try {
      const response = await httpClientInstance.post<InitiatePaymentResponse>(
        API_ENDPOINTS.billing.invoices.initiatePayment(invoiceId),
        {}
      )
      const updated = await httpClientInstance.get<Invoice[]>(API_ENDPOINTS.billing.invoices.list)
      set({ invoices: updated, invoicesLoading: false })
      return response
    } catch (error) {
      set({
        invoicesError: error instanceof Error ? error.message : "Failed to initiate payment",
        invoicesLoading: false,
      })
      throw error
    }
  },

  // ==================== Payments ====================
  payments: [],
  paymentsLoading: false,
  paymentsError: null,

  fetchPayments: async () => {
    set({ paymentsLoading: true, paymentsError: null })
    try {
      const data = await httpClientInstance.get<Payment[]>(API_ENDPOINTS.billing.payments.list)
      set({ payments: data, paymentsLoading: false })
    } catch (error) {
      set({
        paymentsError: error instanceof Error ? error.message : "Failed to fetch payments",
        paymentsLoading: false,
      })
    }
  },

  // ==================== Snapshots ====================
  snapshots: [],
  snapshotsLoading: false,
  snapshotsError: null,

  fetchSnapshots: async () => {
    set({ snapshotsLoading: true, snapshotsError: null })
    try {
      const data = await httpClientInstance.get<BillingSnapshotListItem[]>(API_ENDPOINTS.billing.snapshots.list)
      set({ snapshots: data, snapshotsLoading: false })
    } catch (error) {
      set({
        snapshotsError: error instanceof Error ? error.message : "Failed to fetch billing snapshots",
        snapshotsLoading: false,
      })
    }
  },

  selectedSnapshot: null,
  selectedSnapshotLoading: false,

  fetchSnapshotDetail: async (id) => {
    set({ selectedSnapshotLoading: true })
    try {
      const data = await httpClientInstance.get<BillingSnapshotDetail>(API_ENDPOINTS.billing.snapshots.retrieve(id))
      set({ selectedSnapshot: data, selectedSnapshotLoading: false })
    } catch {
      set({ selectedSnapshot: null, selectedSnapshotLoading: false })
    }
  },

  trend: [],
  trendLoading: false,

  fetchTrend: async (months = 6) => {
    set({ trendLoading: true })
    try {
      const data = await httpClientInstance.get<{ trend_data: SnapshotTrendPoint[] }>(
        `${API_ENDPOINTS.billing.snapshots.trend}?months=${months}`
      )
      set({ trend: data.trend_data, trendLoading: false })
    } catch {
      set({ trend: [], trendLoading: false })
    }
  },
}))

// Selectors
export const selectActiveAlerts = (state: BillingStore) =>
  state.alerts.filter((alert) => alert.status === "ACTIVE" || alert.status === "TRIGGERED")

export const selectPendingOrOverdueInvoices = (state: BillingStore) =>
  state.invoices.filter((inv) => inv.status === "PENDING" || inv.is_overdue)

export const selectTotalOutstandingAmount = (state: BillingStore) =>
  state.invoices
    .filter((inv) => inv.status === "PENDING" || inv.is_overdue)
    .reduce((sum, inv) => sum + parseFloat(inv.total_amount), 0)
